import { Link, isRouteErrorResponse, useParams, useRouteError } from 'react-router-dom';
import { Button } from '@/components';
import { NotFoundPage } from './NotFoundPage';

/** The two messages browsers give when a route's code chunk is gone after a deploy. */
const STALE_CHUNK = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

/**
 * What a route shows when it throws, instead of a white screen.
 *
 * There was no error boundary on any route, so a page that failed to load
 * its code (the normal state of an installed app's tab after a deploy:
 * the old chunk names are gone from the server) or threw while rendering
 * took the whole app with it: no header, no bar, no way back but the
 * browser's own. In the workspace it renders inside the shell, so the
 * header, the rail and the bottom bar are all still there.
 */
export function RouteError({ inShell = false }: { inShell?: boolean }) {
  const error = useRouteError();
  const { orgSlug } = useParams();

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage inShell={inShell} />;
  }

  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : isRouteErrorResponse(error) ? error.statusText : '';
  const stale = STALE_CHUNK.test(message);
  const home = orgSlug ? `/o/${orgSlug}` : '/';

  const body = (
    <div role="alert" className="max-w-prose">
      <h1 className="font-display text-2xl font-semibold leading-tight text-ink sm:text-3xl">
        {stale ? 'A newer version of Claripick is ready' : 'This page could not be shown'}
      </h1>
      <p className="mt-2 text-sm text-ink-2">
        {stale
          ? 'Claripick was updated since this page was opened, and the part of it this screen needs has moved. Reload to get the new version. Nothing you saved is lost.'
          : 'Something on this page went wrong while it was being drawn. Reloading usually clears it. If it happens again, tell support which page you were on.'}
      </p>
      <div className="mt-5 flex flex-wrap gap-2">
        <Button variant="primary" onClick={() => window.location.reload()}>
          Reload
        </Button>
        <Link
          to={home}
          className="inline-flex min-h-tap items-center rounded-ct-sm px-4 text-sm font-semibold text-forest hover:bg-forest-faint active:bg-forest-faint"
        >
          {orgSlug ? 'Go to the workspace home' : 'Go to my workspace'}
        </Link>
      </div>
      {!stale && message ? (
        <details className="mt-6 text-xs text-ink-3">
          <summary className="min-h-tap cursor-pointer py-2">What the browser reported</summary>
          <p className="record break-words">{message}</p>
        </details>
      ) : null}
    </div>
  );

  if (inShell) return <div className="py-6">{body}</div>;
  return (
    <div className="min-h-screen bg-paper">
      <header className="border-b border-white/10 bg-forest-ink pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex max-w-6xl items-center gap-2 px-4 py-2 md:py-3">
          <img src="/logo.svg" alt="" width={28} height={28} className="h-7 w-7 max-w-none brightness-0 invert" />
          <span className="font-display text-lg font-semibold tracking-[-0.02em] text-white">Claripick</span>
        </div>
      </header>
      <main className="mx-auto max-w-2xl px-4 py-12">{body}</main>
    </div>
  );
}
